import { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useMySSOUrl } from '@sentara-health/sentara-api-hooks-core';
import {
  getMaxLength,
  toPascalCase,
  AlphaNumberHypenRegex,
  getDecryptedValue,
  UserDetails,
  formatISODateToMDY,
  isGuest,
  isWellnessUser
} from 'common';
import ApiWrapper from '../../common/ApiWrapper';
import { GlobalContext } from 'context';
import {
  Headings,
  Button,
  Label,
  InputField,
  Disclaimer,
  ErrorMessage,
  Loader,
  Paragraph,
} from '@sentara-health/optimahealth_web';
import { useForm } from 'react-hook-form';

const EnrollInBenefits = () => {
  const { ssoEndPoints } = useContext(GlobalContext);
  const { t } = useTranslation();
  const [loading, setLoading] = useState<any>(false);
  const { SSOUrlType } = useMySSOUrl();
  const { data, callApi } = ApiWrapper(SSOUrlType);

  // Calling a fetchPreferenceSearch API once get a response"
  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors, isValid },
  } = useForm<FormInputs>({}); /* useForm props extract */
  const [personalInfo, setPersonalInfo] = useState<any>({});
  const [isOnlyGuest, setIsOnlyGuest] = useState<boolean>(false);
  interface FormInputs {
    accessCode: string;
  }
 
  /* SSO URL type API call */
  const onSubmit = async (data: FormInputs) => {
    const url = ssoEndPoints?.vlocityEnrollBenefits;
    let urlFormat = url + '&accessCode=' + data.accessCode;
    window?.open(urlFormat, '_blank');
  };

  useEffect(() => {
    if (data) {
      const { ssoUrl } = data;
      window?.open(ssoUrl, '_blank');
    }
  }, [data]);

  useEffect(() => {
    (async () => {
      const personalDetails = await getDecryptedValue(UserDetails.guest);

      const personalInfo = JSON.parse(personalDetails);
      setPersonalInfo(personalInfo.user);
      const guestValue: boolean = await isGuest();
      const wellnessValue: boolean = await isWellnessUser();
      const isOnlyGuest = guestValue && !wellnessValue;
      setIsOnlyGuest(isOnlyGuest);
    })();
  }, []);

  return (
    <>
      {loading && <Loader />}
      <div className="enroll_in_benefits black-color">
        <Headings
          level={1}
          text={t('enrollBenefits.title')}
          variant="semiBold"
          data-testid="enrollBenefitsHeader"
          className="mb-4"
        />
        <Headings
          level={4}
          text={t('common.personalInfo')}
          variant="semiBold"
          data-testid="personalInformationBenefits"
          className="mb-3"
        />
        <div className="row">
          <Paragraph
            text={t('enrollBenefits.firstName')}
            variant="bold"
            data-testid="personalInformationFirstName"
            className="col-xl-2 col-sm-3 col-5 mb-2"
          />
          <Paragraph
            text={toPascalCase(personalInfo?.firstName ?? '')}
            variant="regular"
            data-testid="enrollFirstnameValue"
            className="col-auto mb-sm-0 mb-4"
          />
        </div>
        <div className="row">
          <Paragraph
            text={t('enrollBenefits.lastName')}
            variant="bold"
            data-testid="EnrollLastname"
            className="col-xl-2 col-sm-3 col-5 mb-2"
          />
          <Paragraph
            text={toPascalCase(personalInfo?.lastName ?? '')}
            variant="regular"
            data-testid="enrollLastnameValue"
            className="col-auto mb-sm-0 mb-4"
          />
        </div>
        <div className="row">
          <Paragraph
            text={t('enrollBenefits.dob')}
            variant="bold"
            data-testid="EnrollDob"
            className="col-xl-2 col-sm-3 col-5 mb-2"
          />
          <Paragraph
            text={formatISODateToMDY(personalInfo?.dob ?? '')}
            variant="semibold"
            data-testid="EnrollDobValue"
            className="col-auto mb-sm-0 mb-4"
          />
        </div>
        <Headings
          level={4}
          text={t('enrollBenefits.empInfo')}
          variant="bold"
          data-testid="enrollEmployerInformation"
          className="my-4"
        />
        <form onSubmit={handleSubmit(onSubmit)} method="post">
          <div className="row mb-3">
            <div className="col-lg-6 col-md-6 col-sm-12 col-12">
              <Label
                dataTestId="enterAccessCodeLabel"
                htmlFor="regMemId"
                className="mb-2"
              >
                {t('enrollBenefits.enterAccessCode')}
              </Label>
              <div className="position-relative">
                <InputField
                  id="regMemId"
                  dataTestId="enterAccessCodeInput"
                  type="text"
                  maxLength={getMaxLength.employeeAccessCode}
                  // Remove the duplicate 'onChange' prop
                  placeholder={t('enrollBenefits.placeholderText')}
                  {...register('accessCode', {
                    required: true,
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      setValue(
                        'accessCode',
                        e.target.value.replace(AlphaNumberHypenRegex, '')
                      );
                    },
                  })}
                  className={
                    errors.accessCode
                      ? 'error-border'
                      : ''
                  }
                />
                {errors.accessCode && (
                  <div className="mt-1">
                    <ErrorMessage>{t('enrollBenefits.accessCode')}</ErrorMessage>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="my-4">
            <Disclaimer
              data-testid="disclaimerContent"
              disclaimerContent={t('enrollBenefits.instruction')}
            />
          </div>
          <div className="row d-flex justify-content-start align-items-center py-2">
            <div className="col-sm-auto col-12 mb-sm-0 mb-4">
              <Button
                dataTestId={'submitButton'}
                variant="primary_btn"
                className="btn-md-width"
                disabled={!isValid}
                type="submit"
              >
                {t('common.submit')}
              </Button>
            </div>
            
            { !isOnlyGuest && (
                <div className="col-sm-auto col-12 mb-sm-0">
                  <Button
                    dataTestId={'cancelButton'}
                    onClick={() => window.history.back()}
                    variant="secondary_btn"
                    className="btn-md-width"
                    type="button"
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
              )}
          </div>
        </form>
      </div>
    </>
  );
};

export default EnrollInBenefits;
